// Gmail-style multi-account registry.
//
// The browser may hold up to MAX_USER_SLOTS (8) signed-in sessions. The
// backend stores them in cookies named mc_user_0..mc_user_7; each session
// has its own CSRF token. Which slot a given API request belongs to is
// decided by the X-Account-Slot header that api.js attaches.
//
// Two storage scopes:
//   • localStorage - the roster of known accounts: [{slot, email}, ...].
//     Shared across tabs so the "Switch account" dropdown is consistent
//     everywhere.
//   • sessionStorage - the ACTIVE slot for THIS tab. Per-tab so two tabs
//     can show two different inboxes simultaneously, exactly like Gmail's
//     /u/N/.
//   • localStorage (LAST_ACTIVE_KEY) - the LAST account the user switched to,
//     anywhere. A freshly opened tab or a relaunched installed PWA has empty
//     sessionStorage, so without this it would always snap back to slot 0.
//     We fall back to this last-visited slot instead, so reopening the app
//     resumes the account you were last using.
//
// A new tab inherits the parent tab's sessionStorage on open; refreshing
// the tab keeps its slot. Closing the tab clears its sessionStorage, but the
// last-visited slot persists in localStorage.

export const MAX_USER_SLOTS = 8;
const ROSTER_KEY = "mc_accounts_roster";
const ACTIVE_KEY = "mc_active_slot";       // sessionStorage, per-tab
const LAST_ACTIVE_KEY = "mc_last_active_slot"; // localStorage, cross-session

function readRoster() {
  try {
    const raw = localStorage.getItem(ROSTER_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((a) => Number.isInteger(a?.slot) && typeof a?.email === "string");
  } catch { return []; }
}

function writeRoster(arr) {
  localStorage.setItem(ROSTER_KEY, JSON.stringify(arr));
}

export const accounts = {
  list() { return readRoster(); },

  /** Replace the entire roster (called after /v1/accounts succeeds). */
  setAll(serverAccounts) {
    const clean = (serverAccounts ?? [])
      .filter((a) => Number.isInteger(a?.slot) && typeof a?.email === "string")
      .map((a) => ({ slot: a.slot, email: a.email }));
    writeRoster(clean);
    const slot = this.activeSlot();
    if (!clean.some((a) => a.slot === slot)) sessionStorage.removeItem(ACTIVE_KEY);
  },

  upsert(account) {
    const r = readRoster().filter((a) => a.slot !== account.slot);
    r.push({ slot: account.slot, email: account.email });
    r.sort((a, b) => a.slot - b.slot);
    writeRoster(r);
  },

  remove(slot) {
    writeRoster(readRoster().filter((a) => a.slot !== slot));
  },

  activeSlot() {
    const valid = (v) => {
      const n = Number.parseInt(v ?? "", 10);
      return Number.isFinite(n) && n >= 0 && n < MAX_USER_SLOTS ? n : null;
    };
    // 1) This tab's explicit choice wins (per-tab independence preserved).
    const tabSlot = valid(sessionStorage.getItem(ACTIVE_KEY));
    // 2) Otherwise resume the last account visited in any tab/session.
    const lastSlot = valid(localStorage.getItem(LAST_ACTIVE_KEY));
    const resolved = tabSlot ?? lastSlot ?? 0;
    // 3) Never point at a slot that's no longer signed in. If the roster is
    //    loaded and doesn't contain the resolved slot, fall back to the
    //    lowest signed-in slot.
    const roster = readRoster();
    if (roster.length && !roster.some((a) => a.slot === resolved)) {
      return roster[0].slot;
    }
    return resolved;
  },

  setActiveSlot(slot) {
    const n = Number.parseInt(String(slot ?? 0), 10);
    if (!Number.isFinite(n) || n < 0 || n >= MAX_USER_SLOTS) return;
    sessionStorage.setItem(ACTIVE_KEY, String(n));
    // Remember as the cross-session "last visited" so a future cold launch
    // resumes this account.
    try { localStorage.setItem(LAST_ACTIVE_KEY, String(n)); } catch { /* quota */ }
  },

  nextFreeSlot() {
    const taken = new Set(readRoster().map((a) => a.slot));
    for (let i = 0; i < MAX_USER_SLOTS; i++) if (!taken.has(i)) return i;
    return null;
  },
};

// Pick up ?slot= from the URL on first load and stash it as this tab's
// active slot. Eager at module load so any subsequent api.js call sees it.
const params = new URLSearchParams(window.location.search);
const fromQuery = params.get("slot");
if (fromQuery != null) {
  accounts.setActiveSlot(fromQuery);
  params.delete("slot");
  const qs = params.toString();
  const cleanUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
  window.history.replaceState({}, "", cleanUrl);
}
